import { FC, useEffect, useMemo, useState } from 'react';
import Slider from 'react-slick';
import SanitizedHTML from 'react-sanitized-html';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import styled from 'styled-components';
import { IProviderItemView } from 'store/providers/providerTypes';
import { black, boxShadow1 } from 'utils/style/colors';
import { pictureFormat } from 'utils/check-file-type/listOfFilesFormat';

interface IProps {
  title: string | undefined;
  items: IProviderItemView[];
}

interface ISlideItem {
  thumbnailUrl: string;
  title: string;
  content: string;
  moreInfoLink?: string;
}

const SliderHeader = styled.div`
  line-height: 24px;
  margin: 2rem auto;
  font-size: 20px;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const SliderContainer = styled.div`
  max-width: 1024px;
  /* height: 100%; */
  aspect-ratio: 16/9;
  cursor: pointer;
  margin: 0 auto;

  .slick-slider {
    width: 100% !important;
    height: 100% !important;
  }

  .slick-slide {
    padding: 0.5rem !important;
  }

  .slick-dots {
    bottom: 0 !important;
  }

  .slick-dots button::before {
    line-height: 7rem !important;
    color: ${black} !important;
    font-size: 8px !important;
  }
`;

const Card = styled.div`
  height: 100% !important;
  width: 100% !important;
  display: flex !important;
  flex-direction: column !important;
  padding: 15px !important;
  box-shadow: none !important;
  border-radius: 2.5rem !important;
`;

const CardImage = styled.img`
  aspect-ratio: 16/9;
  object-fit: cover;
  border-radius: 1.5rem;
  box-shadow: ${boxShadow1};
`;

const ItemsSlider: FC<IProps> = ({ title, items }) => {
  const [slideItems, setSlideItems] = useState<ISlideItem[]>([]);

  const sliderSettings = {
    dots: true,
    arrows: false,
    infinite: false,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  const memoizedItems = useMemo(
    () =>
      items.filter((el) =>
        pictureFormat.includes(el?.document?.properties.format)
      ),
    [items]
  );

  useEffect(() => {
    const list = memoizedItems.reduce(
      (acc: ISlideItem[], el: IProviderItemView) => {
        const url = el.url || el.document?.url;
        acc.push({
          thumbnailUrl: url ?? el.thumbnailUrl,
          title: el?.title || el?.name || '',
          content: el?.description || '',
          moreInfoLink: el?.moreInfoUrl,
        });
        return acc;
      },
      []
    );
    setSlideItems(list);
  }, [memoizedItems]);

  const handleOpenUpImage = (moreInfoLink?: string) => {
    if (moreInfoLink) {
      window.open(moreInfoLink);
    }
  };

  return (
    <div style={{ marginBottom: '2rem' }}>
      {title && (
        <SliderHeader>
          <SanitizedHTML allowedAttributes={{ span: ['style'] }} html={title} />
        </SliderHeader>
      )}
      <SliderContainer>
        <Slider {...sliderSettings}>
          {slideItems?.map((slide: ISlideItem, index: number) => (
            <div key={index}>
              <Card className="glass-card">
                <CardImage
                  src={slide.thumbnailUrl}
                  onClick={() => handleOpenUpImage(slide?.moreInfoLink)}
                />
              </Card>
            </div>
          ))}
        </Slider>
      </SliderContainer>
    </div>
  );
};

export default ItemsSlider;
